import { useState, useRef, memo, useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import { RacePhaseCarousel } from '@/components/RacePhaseCarousel';
import { CountdownBanner } from '@/components/CountdownBanner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Edit, Trash2, Printer, Copy, ChevronDown, ChevronRight, Share, MoreVertical, CheckCheck, CalendarIcon } from 'lucide-react';

interface Phase {
  id: string;
  title: string;
  isPlaceholder?: boolean;
}

interface ChecklistItem {
  id: string;
  item: string;
  comment: string;
}

interface SectionProps {
  id: string;
  name: string;
  phases: Phase[];
  phaseTitles: Record<string, string>;
  onPhaseSelect: (phaseId: string, phaseTitle: string) => void;
  onSectionNameUpdate: (sectionId: string, newName: string) => void;
  onRemoveSection: (sectionId: string) => void;
  onAddTile: (sectionId: string) => void;
  onRemoveTile: (sectionId: string, phaseId: string) => void;
  onReorderPhases: (sectionId: string, phases: Phase[]) => void;
  canRemoveSection: boolean;
  onPrintSection: (sectionId: string) => void;
  onCopySection?: (sectionId: string) => void;
  isLoggedIn: boolean;
  targetDate?: Date | null;
  countdownEnabled?: boolean;
  onCountdownUpdate?: (sectionId: string, targetDate: Date | null, enabled: boolean) => void;
  isSampleChecklist?: boolean;
  externalCollapsed?: boolean;
  onExternalToggleCollapse?: () => void;
  onShareSection?: (sectionId: string) => void;
  phaseItems?: Record<string, ChecklistItem[]>;
  onClearSectionCheckboxes?: (sectionId: string) => void;
}

const SectionComponent = ({
  id,
  name,
  phases,
  phaseTitles,
  onPhaseSelect,
  onSectionNameUpdate,
  onRemoveSection,
  onAddTile,
  onRemoveTile,
  onReorderPhases,
  canRemoveSection,
  onPrintSection,
  onCopySection,
  isLoggedIn,
  targetDate,
  countdownEnabled = false,
  onCountdownUpdate,
  isSampleChecklist = false,
  externalCollapsed,
  onExternalToggleCollapse,
  onShareSection,
  phaseItems,
  onClearSectionCheckboxes
}: SectionProps) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [isMobileEditDialogOpen, setIsMobileEditDialogOpen] = useState(false);
  const [editedName, setEditedName] = useState(name);
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const [isClearCheckboxesDialogOpen, setIsClearCheckboxesDialogOpen] = useState(false);
  const [showDatePickerPopover, setShowDatePickerPopover] = useState(false);
  const [showMobileDateDialog, setShowMobileDateDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(targetDate || undefined);
  const [selectedTime, setSelectedTime] = useState(targetDate ? format(targetDate, 'HH:mm') : '07:00');
  const [selectedTimezone, setSelectedTimezone] = useState('America/Chicago');
  const [isSavingDate, setIsSavingDate] = useState(false);
  
  // Use external collapsed state if provided, otherwise use internal state
  const isCollapsed = externalCollapsed !== undefined ? externalCollapsed : internalCollapsed;
  const toggleCollapsed = onExternalToggleCollapse || (() => setInternalCollapsed(!internalCollapsed));

  const handleNameSave = useCallback(() => {
    if (editedName.trim()) {
      onSectionNameUpdate(id, editedName.trim());
    } else {
      setEditedName(name);
    }
    setIsEditingName(false);
    setIsMobileEditDialogOpen(false);
  }, [editedName, id, name, onSectionNameUpdate]);

  const handleNameKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleNameSave();
    } else if (e.key === 'Escape') {
      setEditedName(name);
      setIsEditingName(false);
      setIsMobileEditDialogOpen(false);
    }
  }, [handleNameSave, name]);

  const handleMobileEditClick = useCallback(() => {
    setIsMobileEditDialogOpen(true);
  }, []);

  const handleMobileEditCancel = useCallback(() => {
    setEditedName(name);
    setIsMobileEditDialogOpen(false);
  }, [name]);

  const handlePlaceholderClick = useCallback(() => {
    if (!isLoggedIn && !isSampleChecklist) {
      // Authentication check will be handled by parent component
    }
    onAddTile(id);
  }, [id, isLoggedIn, isSampleChecklist, onAddTile]);

  const handleCountdownUpdate = useCallback((newTargetDate: Date | null, enabled: boolean) => {
    if (!isLoggedIn && id === 'default-section') {
      return;
    }
    if (onCountdownUpdate) {
      onCountdownUpdate(id, newTargetDate, enabled);
    }
  }, [id, isLoggedIn, onCountdownUpdate]);

  const handleSaveDateTime = useCallback(async () => {
    if (selectedDate) {
      setIsSavingDate(true);
      try {
        const [hours, minutes] = selectedTime.split(':').map(Number);
        const localDate = new Date(selectedDate);
        localDate.setHours(hours, minutes, 0, 0);
        const utcDate = fromZonedTime(localDate, selectedTimezone);
        
        if (!isLoggedIn) {
          sessionStorage.setItem('guest-countdown', JSON.stringify({
            targetDate: utcDate.toISOString(),
            enabled: true
          }));
        }
        
        handleCountdownUpdate(utcDate, true);
        setShowDatePickerPopover(false);
        setShowMobileDateDialog(false);
      } finally {
        setIsSavingDate(false);
      }
    }
  }, [selectedDate, selectedTime, selectedTimezone, isLoggedIn, handleCountdownUpdate]);

  const showSetDateButton = !isSampleChecklist && !countdownEnabled && (isLoggedIn || id !== 'default-section');

  const handleRemoveTile = useCallback((phaseId: string) => {
    onRemoveTile(id, phaseId);
  }, [id, onRemoveTile]);

  const handleReorderPhases = useCallback((reorderedPhases: Phase[]) => {
    onReorderPhases(id, reorderedPhases);
  }, [id, onReorderPhases]);

  const actualPhases = useMemo(() => phases.filter(phase => !phase.isPlaceholder), [phases]);
  const placeholderPhase = useMemo(() => phases.find(phase => phase.isPlaceholder), [phases]);

  return (
    <TooltipProvider>
      <Collapsible open={!isCollapsed} onOpenChange={(open) => toggleCollapsed()}>
      <Card
        className={`mb-8 border border-border border-l-4 border-l-primary bg-card relative z-20 transition-colors duration-200 hover:border-accent pt-1`}
        style={{ 
          minHeight: isCollapsed ? 'auto' : '320px'
        }}
      >
        <CardHeader className="px-10 py-2 lg:py-4 space-y-0">
          {/* Desktop layout */}
          <div className="hidden lg:flex items-center w-full gap-2">
            {/* Zone 1: Collapse Arrow - anchored left */}
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-11 w-11 p-0 hover:bg-muted min-h-[44px] min-w-[44px] flex-shrink-0 -ml-6"
                aria-label={isCollapsed ? `Expand ${name} section` : `Collapse ${name} section`}
              >
                {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </CollapsibleTrigger>

            {/* Zone 2: Name + Edit Button - fills available space */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {!isSampleChecklist && isEditingName ? (
                <Input
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value.slice(0, 40))}
                  onBlur={handleNameSave}
                  onKeyDown={handleNameKeyPress}
                  className="text-xl font-normal bg-background/50 border-primary/20 w-[420px]"
                  maxLength={40}
                  autoFocus
                  id={`section-name-${id}`}
                  aria-label="Edit event name"
                />
              ) : (
                <h2 
                  className={`text-xl font-normal text-foreground truncate ${!isSampleChecklist && (isLoggedIn || id !== 'default-section') ? 'cursor-pointer hover:text-primary transition-colors' : ''}`}
                  onClick={!isSampleChecklist && (isLoggedIn || id !== 'default-section') ? () => setIsEditingName(true) : undefined}
                  title={name}
                  role={!isSampleChecklist && (isLoggedIn || id !== 'default-section') ? "button" : undefined}
                  tabIndex={!isSampleChecklist && (isLoggedIn || id !== 'default-section') ? 0 : undefined}
                  aria-label={!isSampleChecklist && (isLoggedIn || id !== 'default-section') ? `Edit event name: ${name}` : name}
                  onKeyDown={!isSampleChecklist && (isLoggedIn || id !== 'default-section') ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setIsEditingName(true);
                    }
                  } : undefined}
                >
                  {name}
                </h2>
              )}
              {!isSampleChecklist && (isLoggedIn || id !== 'default-section') && (
                <Tooltip>
                  <TooltipTrigger asChild>
                  <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsEditingName(true)}
                      className="h-11 w-11 p-0 hover:bg-muted flex-shrink-0 min-h-[44px] min-w-[44px]"
                      aria-label={`Edit event name for ${name}`}
                    >
                      <Edit className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="z-[99999]">
                    <p>Edit event name</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>

            {/* Zone 3: Action Icons - anchored right */}
            <div className="flex items-center gap-1 flex-shrink-0 -mr-6">
                {showSetDateButton && (
                  <Tooltip>
                    <Popover open={showDatePickerPopover} onOpenChange={setShowDatePickerPopover}>
                      <PopoverTrigger asChild>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-11 w-11 p-0"
                            aria-label="Set event date"
                          >
                            <CalendarIcon className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        </TooltipTrigger>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="end">
                        <div className="p-4 space-y-4">
                          <CalendarComponent
                            mode="single"
                            selected={selectedDate}
                            onSelect={(date) => date && setSelectedDate(date)}
                            disabled={(date) => date < new Date()}
                            initialFocus
                          />
                          {selectedDate && (
                            <div className="space-y-3 border-t pt-3">
                              <div className="space-y-2">
                                <Label htmlFor={`event-time-${id}`} className="font-normal">Time</Label>
                                <Input
                                  id={`event-time-${id}`}
                                  type="time"
                                  value={selectedTime}
                                  onChange={(e) => setSelectedTime(e.target.value)}
                                  className="w-full font-normal"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor={`event-tz-${id}`} className="font-normal">Timezone</Label>
                                <Select value={selectedTimezone} onValueChange={setSelectedTimezone}>
                                  <SelectTrigger className="font-normal">
                                    <SelectValue placeholder="Select timezone" />
                                  </SelectTrigger>
                                  <SelectContent className="font-normal">
                                    <SelectItem value="America/New_York">Eastern Time</SelectItem>
                                    <SelectItem value="America/Chicago">Central Time</SelectItem>
                                    <SelectItem value="America/Denver">Mountain Time</SelectItem>
                                    <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                                    <SelectItem value="UTC">UTC</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <Button
                                variant="accent"
                                onClick={handleSaveDateTime}
                                className="w-full font-normal"
                                disabled={isSavingDate}
                              >
                                {isSavingDate ? 'Saving...' : 'Set Event Date & Time'}
                              </Button>
                            </div>
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                    <TooltipContent className="z-[99999]">Set event date</TooltipContent>
                  </Tooltip>
                )}
                {isSampleChecklist && onCopySection && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        onClick={() => onCopySection(id)} 
                        variant="ghost" 
                        size="sm" 
                        className="h-11 w-11 p-0"
                        aria-label="Copy to My Event Checklists"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Copy to My Event</TooltipContent>
                  </Tooltip>
                )}
                {!isSampleChecklist && onCopySection && (isLoggedIn || id !== 'default-section') && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        onClick={() => onCopySection(id)} 
                        variant="ghost" 
                        size="sm" 
                        className="h-11 w-11 p-0"
                        aria-label="Copy event"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Copy event</TooltipContent>
                  </Tooltip>
                )}
                {!isSampleChecklist && onShareSection && (isLoggedIn || id !== 'default-section') && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        onClick={() => onShareSection(id)} 
                        variant="ghost" 
                        size="sm" 
                        className="h-11 w-11 p-0"
                        aria-label="Share event"
                      >
                        <Share className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Share event</TooltipContent>
                  </Tooltip>
                )}
                {!isSampleChecklist && onClearSectionCheckboxes && (isLoggedIn || id !== 'default-section') && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        onClick={() => setIsClearCheckboxesDialogOpen(true)} 
                        variant="ghost" 
                        size="sm" 
                        className="h-11 w-11 p-0"
                        aria-label="Clear all checkboxes"
                      >
                        <CheckCheck className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Clear all checkboxes</TooltipContent>
                  </Tooltip>
                )}
                {!isSampleChecklist && (isLoggedIn || id !== 'default-section') && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        onClick={() => onPrintSection(id)} 
                        variant="ghost" 
                        size="sm" 
                        className="h-11 w-11 p-0"
                        aria-label="Print checklists"
                      >
                        <Printer className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Print checklists</TooltipContent>
                  </Tooltip>
                )}
                {canRemoveSection && (
                  <AlertDialog>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <AlertDialogTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-11 w-11 p-0 hover:bg-destructive hover:text-destructive-foreground text-destructive"
                            aria-label="Remove event"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                      </TooltipTrigger>
                      <TooltipContent>Remove event</TooltipContent>
                    </Tooltip>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove Event</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to remove this event? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => onRemoveSection(id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                          Remove
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </div>

          {/* Mobile/Tablet layout */}
          <div className="flex flex-col lg:hidden">
            <div className="flex items-center w-full gap-2">
              {/* Zone 1: Collapse Arrow */}
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 hover:bg-muted flex-shrink-0 -ml-6"
                  aria-label={isCollapsed ? "Expand event" : "Collapse event"}
                >
                  {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              </CollapsibleTrigger>

              {/* Zone 2: Name + Edit Button */}
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <h2 
                  className={`text-xl font-normal text-foreground truncate flex-1 min-w-0 h-8 flex items-center ${!isSampleChecklist && (isLoggedIn || id !== 'default-section') ? 'cursor-pointer hover:text-primary transition-colors' : ''}`}
                  onClick={!isSampleChecklist && (isLoggedIn || id !== 'default-section') ? handleMobileEditClick : undefined}
                  title={name}
                  role={!isSampleChecklist && (isLoggedIn || id !== 'default-section') ? "button" : undefined}
                  tabIndex={!isSampleChecklist && (isLoggedIn || id !== 'default-section') ? 0 : undefined}
                  aria-label={!isSampleChecklist && (isLoggedIn || id !== 'default-section') ? `Edit event name: ${name}` : name}
                  onKeyDown={!isSampleChecklist && (isLoggedIn || id !== 'default-section') ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleMobileEditClick();
                    }
                  } : undefined}
                >
                  {name}
                </h2>
                {!isSampleChecklist && (isLoggedIn || id !== 'default-section') && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleMobileEditClick}
                        className="h-8 w-8 p-0 hover:bg-muted flex-shrink-0"
                        aria-label={`Edit event name for ${name}`}
                      >
                        <Edit className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="z-[99999]">
                      <p>Edit event name</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>

              {/* Mobile Edit Dialog */}
              <Dialog open={isMobileEditDialogOpen} onOpenChange={setIsMobileEditDialogOpen}>
                <DialogContent className="w-[90vw] max-w-md">
                  <DialogHeader>
                    <DialogTitle>Edit Event Name</DialogTitle>
                  </DialogHeader>
                  <div className="py-4">
                    <Input
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value.slice(0, 40))}
                      onKeyDown={handleNameKeyPress}
                      className="text-lg"
                      maxLength={40}
                      autoFocus
                      placeholder="Enter event name"
                    />
                    <p className="text-sm text-muted-foreground mt-2">
                      {editedName.length}/40 characters
                    </p>
                  </div>
                  <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={handleMobileEditCancel}>
                      Cancel
                    </Button>
                    <Button onClick={handleNameSave} variant="accent">
                      Save Changes
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
                   
              {/* More Actions Dropdown - Mobile Only */}
              {/* Zone 3: 3-dot Menu - anchored right */}
              <DropdownMenu open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 flex-shrink-0 -mr-6">
                    <MoreVertical className="h-4 w-4" />
                    <span className="sr-only">More actions</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-card border border-border z-[99999]">
                  {showSetDateButton && (
                    <DropdownMenuItem onSelect={(e) => {
                      e.preventDefault();
                      setShowMobileDateDialog(true);
                      setIsMobileMenuOpen(false);
                    }}>
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      Set Event Date
                    </DropdownMenuItem>
                  )}
                  {isSampleChecklist && onCopySection && (
                    <DropdownMenuItem onClick={() => onCopySection(id)}>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy to My Event
                    </DropdownMenuItem>
                  )}
                  {!isSampleChecklist && onCopySection && (isLoggedIn || id !== 'default-section') && (
                    <DropdownMenuItem onClick={() => onCopySection(id)}>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy Event
                    </DropdownMenuItem>
                  )}
                  {!isSampleChecklist && onShareSection && (isLoggedIn || id !== 'default-section') && (
                    <DropdownMenuItem onClick={() => onShareSection(id)}>
                      <Share className="h-4 w-4 mr-2" />
                      Share Event
                    </DropdownMenuItem>
                  )}
                  {!isSampleChecklist && onClearSectionCheckboxes && (isLoggedIn || id !== 'default-section') && (
                    <DropdownMenuItem onClick={() => {
                      setIsClearCheckboxesDialogOpen(true);
                      setIsMobileMenuOpen(false);
                    }}>
                      <CheckCheck className="h-4 w-4 mr-2" />
                      Clear All Checkboxes
                    </DropdownMenuItem>
                  )}
                  {!isSampleChecklist && (isLoggedIn || id !== 'default-section') && (
                    <DropdownMenuItem onClick={() => onPrintSection(id)}>
                      <Printer className="h-4 w-4 mr-2" />
                      Print Checklists
                    </DropdownMenuItem>
                  )}
                  {canRemoveSection && (
                    <DropdownMenuItem onSelect={(e) => {
                      e.preventDefault();
                      setIsRemoveDialogOpen(true);
                      setIsMobileMenuOpen(false);
                    }} className="text-destructive focus:text-destructive">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove Event
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile Date Picker Dialog */}
              {showSetDateButton && (
                <Dialog open={showMobileDateDialog} onOpenChange={setShowMobileDateDialog}>
                  <DialogContent className="w-[90vw] max-w-md">
                    <DialogHeader>
                      <DialogTitle>Set Event Date</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <CalendarComponent
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => date && setSelectedDate(date)}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                      {selectedDate && (
                        <div className="space-y-3 border-t pt-3">
                          <div className="space-y-2">
                            <Label htmlFor={`mobile-event-time-${id}`} className="font-normal">Time</Label>
                            <Input
                              id={`mobile-event-time-${id}`}
                              type="time"
                              value={selectedTime}
                              onChange={(e) => setSelectedTime(e.target.value)}
                              className="w-full font-normal"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor={`mobile-event-tz-${id}`} className="font-normal">Timezone</Label>
                            <Select value={selectedTimezone} onValueChange={setSelectedTimezone}>
                              <SelectTrigger className="font-normal">
                                <SelectValue placeholder="Select timezone" />
                              </SelectTrigger>
                              <SelectContent className="font-normal">
                                <SelectItem value="America/New_York">Eastern Time</SelectItem>
                                <SelectItem value="America/Chicago">Central Time</SelectItem>
                                <SelectItem value="America/Denver">Mountain Time</SelectItem>
                                <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                                <SelectItem value="UTC">UTC</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <Button
                            variant="accent"
                            onClick={handleSaveDateTime}
                            className="w-full font-normal"
                            disabled={isSavingDate}
                          >
                            {isSavingDate ? 'Saving...' : 'Set Event Date & Time'}
                          </Button>
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              )}

              {/* Remove Event Confirmation Dialog - Outside dropdown to prevent unmounting */}
              {canRemoveSection && (
                <AlertDialog open={isRemoveDialogOpen} onOpenChange={setIsRemoveDialogOpen}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Remove Event</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to remove this event? This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => onRemoveSection(id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                        Remove
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              
              {/* Clear Checkboxes Confirmation Dialog */}
              {!isSampleChecklist && onClearSectionCheckboxes && (isLoggedIn || id !== 'default-section') && (
                <AlertDialog open={isClearCheckboxesDialogOpen} onOpenChange={setIsClearCheckboxesDialogOpen}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Clear All Checkboxes in "{name}"</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to clear all checkboxes for this event?
              </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => {
                        onClearSectionCheckboxes(id);
                        setIsClearCheckboxesDialogOpen(false);
                      }}>
                        Clear All
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
          </CardHeader>

          <CollapsibleContent>
            <CardContent className="pt-0">
              <CountdownBanner
                targetDate={targetDate || null}
                countdownEnabled={countdownEnabled}
                onDateChange={isSampleChecklist ? undefined : (date) => handleCountdownUpdate(date, date !== null)}
                onToggleCountdown={isSampleChecklist ? undefined : (enabled) => handleCountdownUpdate(targetDate, enabled)}
                eventName={name}
                isLoggedIn={isLoggedIn}
                readOnly={isSampleChecklist}
              />
              <RacePhaseCarousel
                phases={actualPhases}
                placeholderPhase={placeholderPhase}
                onPhaseSelect={onPhaseSelect}
                onRemoveTile={isSampleChecklist ? undefined : handleRemoveTile}
                onReorderPhases={isSampleChecklist ? undefined : handleReorderPhases}
                onPlaceholderClick={isSampleChecklist ? undefined : handlePlaceholderClick}
                phaseTitles={phaseTitles}
                isStaticReference={isSampleChecklist}
                showLoginPrompt={!isLoggedIn}
                phaseItems={phaseItems}
              />
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </TooltipProvider>
  );
};

export const Section = memo(SectionComponent);